import BaseController from "../../controller/BaseController";
import PersonalizationUtils from "./utils/PersonalizationUtils";
import Subtask from "./Subtask";
import DialogUtils from "./utils/DialogUtils";
import { Dialog$AfterCloseEvent, Dialog$BeforeOpenEvent } from "sap/m/Dialog";
import JSONModel from "sap/ui/model/json/JSONModel";
export default abstract class PersonalizableSubtask extends Subtask {
	protected personalizationData: Record<string, unknown> = {};
	constructor(controller: BaseController, subtaskId: string) {
		super(controller, subtaskId);
	}
	
	/**
	 * Abstract method to apply personalization data to the subtask.
	 * This method should be implemented by subclasses to define how personalization data is applied.
	 */
	public abstract applyPersonalization(): void;
	
	/**
	 * Abstract method to handle the before open event of the personalization dialog.
	 * This method should be implemented by subclasses to define specific behavior before the dialog opens.
	 * @param {Dialog$BeforeOpenEvent} event  - The before open event of the dialog. 
	 */
	public abstract onPersonalizationDialogBeforeOpen(event: Dialog$BeforeOpenEvent): void;
	
	/**
	 * Abstract method to handle the after close event of the personalization dialog.
	 * This method should be implemented by subclasses to define specific behavior after the dialog closes.
	 * @param {Dialog$AfterCloseEvent} event - The after close event of the dialog.
	 */
	public abstract onPersonalizationDialogAfterClose(event: Dialog$AfterCloseEvent): void;
	
	/**
	 * Abstract method to create default personalization data for the subtask.
	 * This method should be implemented by subclasses to define the default personalization data structure.
	 * @returns {Record<string, unknown>} - The default personalization data for the subtask.
	 */
	public abstract createDefaultPersonalizationData(): Record<string, unknown>;

	/**
	 * Initializes the personalization data for the subtask.
	 * This method retrieves the personalization data from the model and applies it.
	 * If no personalization data is found, it initializes with default data.
	 */
	protected initPersonalization(): void {
		// Load personalization data for the subtask
		const personalizationModel = this.controller.getOwnerComponent().getModel("personalization") as JSONModel;
		if (!personalizationModel) {
			console.error("Personalization model not found.");
			return;
		}
		const personalizationModelData = personalizationModel.getData() as Record<string, unknown>;
		if (personalizationModelData && typeof personalizationModelData[this.metadata.subtaskId] === "object") {
			
			this.personalizationData = personalizationModelData[this.metadata.subtaskId] as Record<string, unknown>;
		}
		else {
			console.warn(`No personalization data found for subtask ${this.metadata.subtaskId}. Initializing with empty object.`);
			this.personalizationData = this.createDefaultPersonalizationData();
		}
		this.applyPersonalization();
	}

    /**
     * Opens the personalization dialog for the subtask.
     * The dialog is always named personalizationDialog<SubtaskType>.
     * This method is intended to be overridden by subclasses to implement specific personalization logic.
     * @throws Error if the personalization dialog cannot be opened.
     */
	public openPersonalizationDialog(beforeOpenCallback: (event: Dialog$BeforeOpenEvent) => void, afterCloseCallback: (event: Dialog$AfterCloseEvent) => void): void {
        DialogUtils.openDialog(
            this.controller,
            `sap.mcsuite.view.fragments.personalization.${this.metadata.name}`,
            beforeOpenCallback,
            afterCloseCallback
        ).catch((error: Error) => {
            console.error("Error opening personalization dialog:", error);
            throw new Error(`Failed to open personalization dialog: ${error.message}`);
        });
	}

	/**
	 * Saves the personalization data for the subtask.
	 * This method uses the PersonalizationUtils to save the data and then applies it.
	 * If an error occurs during saving, it logs the error to the console.
	 * @throws Error if there is an issue saving the personalization data.
	 * */
	public savePersonalization(): void {
		
		PersonalizationUtils.savePersonalizationForSubtask(this.controller, this.metadata.subtaskId, this.personalizationData)
			.then(() => {
				this.applyPersonalization();
			})
			.catch((error: Error) => {
				console.error("Error saving personalization:", error);
				throw new Error(`Failed to save personalization: ${error.message}`);
			});
	}

	/**
	 * Resets the personalization data for the subtask.
	 * This method reloads the personalization data from the model and applies it.
	 * If an error occurs during loading, it logs the error to the console.
	 * @throws Error if there is an issue loading the personalization data.
	 * */
	public resetPersonalization(): void {
		PersonalizationUtils.loadPersonalization(this.controller)
			.then(() => {
				this.applyPersonalization();
			})
			.catch((error: Error) => {
				console.error("Error loading personalization:", error);
				throw new Error(`Failed to load personalization: ${error.message}`);
			});
	}

}
